import React, { useState } from 'react';
import { PencilLine, CircleFadingArrowUp, RefreshCcw } from 'lucide-react';
import { NodeStruct } from '@/types/node-types';
import { Badge } from '@/components/ui/badge';
import { TruncateTextWithTooltip } from '@/components/ui/truncate-text-with-tooltip';
import { Button } from '@/components/ui/button';
import { NodeAttrEditModal } from './node-attr-edit-modal';
import { cn } from '@/lib/utils';
import { IconTile } from '../../icon-tile';
import { getNodeIconColor } from '@/constants/node-styling';
import {
  isNodeDeprecated,
  getDeprecationMessage,
  TOOLTIP_DELAY_MS,
} from '@/constants/deprecated-nodes';
import { DeprecationNotice } from '@/components/deprecation-notice';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface NodeHeaderProps {
  struct: NodeStruct;
  onSidebar?: boolean;
  onNodeUpdate?: (updates: {
    user_config_name?: string;
    user_config_description?: string;
    icon?: string;
    icon_color?: string;
  }) => void;
  className?: string;
  isEditable?: boolean;
  updateAvailable?: boolean;
  onUpdateNode?: () => void;
}

export const NodeHeader: React.FC<NodeHeaderProps> = ({
  struct,
  onSidebar = false,
  onNodeUpdate,
  className,
  isEditable = true,
  updateAvailable = false,
  onUpdateNode,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const iconColor = getNodeIconColor(struct.node_type, struct.icon_color);
  const useThemeColors = iconColor === null;
  const isDeprecated = isNodeDeprecated(struct.display_name);
  const deprecationMessage = getDeprecationMessage(struct.display_name);

  const displayName = struct.user_config_name || struct.display_name;
  const displayDescription =
    struct.user_config_description ?? struct.description;

  const handleOpenModal = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
  };

  const showUpdateBelowHeader = updateAvailable && onSidebar;
  const showUpdateIconInTitle = updateAvailable && !onSidebar;

  const handleSave = (updates: {
    user_config_name?: string;
    user_config_description?: string;
    icon?: string;
    icon_color?: string;
  }) => {
    if (onNodeUpdate) {
      onNodeUpdate(updates);
    }
    setIsEditModalOpen(false);
  };

  return (
    <>
      <div
        className={cn(
          'node-header__wrapper hover:cursor-pointer group',
          className,
        )}
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => setIsHovered(false)}
        onFocusCapture={() => setIsHovered(true)}
        onBlurCapture={() => setIsHovered(false)}
      >
        <div className='node-header'>
          {struct.icon && (
            <IconTile
              icon={struct.icon}
              backgroundColor={iconColor || ''}
              iconColor={iconColor ? '#ffffff' : ''}
              size='small'
              style={
                !useThemeColors && iconColor
                  ? {
                      background: `linear-gradient(to bottom, ${iconColor}, color-mix(in srgb, ${iconColor} 85%, black) )`,
                    }
                  : undefined
              }
              className={
                useThemeColors
                  ? 'bg-linear-to-b from-slate-700 to-slate-900 dark:from-white dark:to-slate-200'
                  : ''
              }
              iconClassName={
                useThemeColors
                  ? 'size-5 text-white dark:text-slate-950'
                  : 'size-5'
              }
            />
          )}
          <div className='flex items-center min-w-0 overflow-hidden flex-1'>
            <TruncateTextWithTooltip
              text={displayName}
              maxLines={1}
              className='font-medium text-base'
              tooltipProps={{
                side: onSidebar ? 'left' : 'top',
                align: onSidebar ? 'start' : 'center',
                sideOffset: onSidebar ? 50 : 12,
                className: 'max-w-xs',
              }}
            />
            {showUpdateIconInTitle ? (
              <Tooltip delayDuration={TOOLTIP_DELAY_MS}>
                <TooltipTrigger asChild>
                  <button
                    type='button'
                    className='ml-1.5 shrink-0 inline-flex border-0 bg-transparent p-0 text-[#10B981] dark:text-emerald-400'
                    aria-label='Update available'
                  >
                    <CircleFadingArrowUp className='size-4' />
                  </button>
                </TooltipTrigger>
                <TooltipContent side='top' align='center' sideOffset={8}>
                  Update Available
                </TooltipContent>
              </Tooltip>
            ) : !showUpdateBelowHeader && isDeprecated ? (
              <Badge
                variant='secondary'
                className='text-xs ml-1.5 shrink-0 text-red-600 bg-red-100'
              >
                Deprecated
              </Badge>
            ) : !showUpdateBelowHeader && struct.beta ? (
              <Badge
                variant='secondary'
                className='text-xs ml-1.5 shrink-0 text-yellow-600 bg-yellow-100'
              >
                Beta
              </Badge>
            ) : showUpdateBelowHeader && isDeprecated ? (
              <Badge
                variant='secondary'
                className='text-xs ml-1.5 shrink-0 text-red-600 bg-red-100'
              >
                Deprecated
              </Badge>
            ) : showUpdateBelowHeader && struct.beta ? (
              <Badge
                variant='secondary'
                className='text-xs ml-1.5 shrink-0 text-yellow-600 bg-yellow-100'
              >
                Beta
              </Badge>
            ) : null}
            {/* Spacer to push pencil button to the right */}
            <div className='flex-1' />
            {/* Pencil icon button - positioned next to display_name for generic-node, only added on hover */}
            {isEditable && !onSidebar && isHovered && (
              <Button
                variant='ghost'
                size='sm'
                className='shrink-0 size-5 p-0'
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenModal();
                }}
              >
                <PencilLine className='h-3.5 w-3.5' />
              </Button>
            )}
          </div>

          {/* Pencil icon button - positioned on right of entire header for console-sidebar */}
          {isEditable && onSidebar && (
            <Button
              variant='ghost'
              size='sm'
              className='shrink-0 h-8 w-8 p-0'
              onClick={handleOpenModal}
            >
              <PencilLine className='h-4 w-4' />
            </Button>
          )}
        </div>
        {isDeprecated && deprecationMessage ? (
          <DeprecationNotice
            message={deprecationMessage}
            compact={onSidebar}
            className='mt-2'
          />
        ) : (
          displayDescription && (
            <TruncateTextWithTooltip
              text={displayDescription}
              interpretPlainTextFormat
              maxLines={3}
              className='text-sm text-muted-foreground font-normal mt-2'
              tooltipDelay={TOOLTIP_DELAY_MS}
              tooltipProps={{
                side: onSidebar ? 'left' : 'right',
                align: 'start',
                sideOffset: onSidebar ? 50 : 12,
                className: 'max-w-xs',
              }}
            />
          )
        )}
        {showUpdateBelowHeader && (
          <div
            role='status'
            aria-label='Update available'
            className='mt-2 flex w-full min-w-0 items-center gap-2 rounded-md bg-[#10B981]/16 px-2 py-1.5 text-emerald-950 dark:bg-[#10B981]/12 dark:text-emerald-200'
          >
            <div className='flex min-w-0 items-center gap-2'>
              <RefreshCcw className='size-4 shrink-0' />
              <span className='min-w-0 truncate text-xs leading-normal'>
                Update Available
              </span>
            </div>
            {onUpdateNode && isEditable && (
              <Button
                variant='ghost'
                size='default'
                className='ml-auto shrink-0 h-8 px-2 gap-1 text-emerald-800 hover:bg-emerald-800 hover:text-white dark:text-emerald-300 dark:hover:bg-emerald-800/55 dark:hover:text-white'
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateNode();
                }}
              >
                <CircleFadingArrowUp className='size-4' />
                Update
              </Button>
            )}
          </div>
        )}
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <NodeAttrEditModal
          isOpen={isEditModalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
          nodeName={struct.display_name}
          nodeDescription={struct.description}
          nodeIcon={struct.icon}
          nodeIconColor={struct.icon_color}
          userConfigName={struct.user_config_name}
          userConfigDescription={struct.user_config_description}
          nodeNameId={struct.node_id}
        />
      </div>
    </>
  );
};
